<script>
	import CustomButton from "../../../components/CustomButton/CustomButton.svelte";
	import ErrorMessageHandler from "../../../components/ErrorMessageHandler/ErrorMessageHandler.svelte";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import Tabs from "../../../components/Tabs/Tabs.svelte";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import NewCaseFilterForm from "./New/NewCaseFilterForm.svelte";
	import NewCaseTable from "./New/NewCaseTable.svelte";
	import { navigate } from "svelte-routing";
	import client from "src/services/client";
	import { extractErrorMessage } from "src/utils/misc";
	import {
		initPage,
		loading,
		sizeSelect,
		totalRecords,
		totalPages,
		damageReviewedData,
		caseExportEnabled,
		caseExportList,
		errorMessage,
	} from "src/store/store";
	import queryString from "query-string";
	import { onMount, onDestroy } from "svelte";
	import UnderInvestigationFilterForm from "./UnderInvestigation/UnderInvestigationFilterForm.svelte";
	import ToQuoteFilterForm from "./ToQuote/ToQuoteFilterForm.svelte";
	import ToBillFilterForm from "./ToBill/ToBillFilterForm.svelte";
	import RejectedFilterForm from "./Rejected/RejectedFilterForm.svelte";
	import UnderInvestigationTable from "./UnderInvestigation/UnderInvestigationTable.svelte";
	import ToQuoteTable from "./ToQuote/ToQuoteTable.svelte";
	import ToBillTable from "./ToBill/ToBillTable.svelte";
	import RejectedTable from "./Rejected/RejectedTable.svelte";
	import AllCaseFilterForm from "./All/AllCaseFilterForm.svelte";
	import AllCaseTable from "./All/AllCaseTable.svelte";
	import ClosedFilterForm from "./Closed/ClosedFilterForm.svelte";
	import ClosedTable from "./Closed/ClosedTable.svelte";
	import { exportCaseByCaseNo } from "src/services/fmsTasking";
	import ApprovingTable from "./Approving/ApprovingTable.svelte";

	let items = [
		{
			label: "All",
			value: "",
		},
		{
			label: "New",
			value: "New",
		},
		{
			label: "Investigating",
			value: "Investigating",
		},
		{
			label: "Approving",
			value: "Approving",
		},
		{
			label: "Quoting",
			value: "Quoting",
		},
		{
			label: "Pre Billing",
			value: "PreBilling",
		},
		{
			label: "Closed",
			value: "Closed",
		},
		{
			label: "Rejected",
			value: "Rejected",
		},
	];

	const filterForms = {
		All: AllCaseFilterForm,
		New: NewCaseFilterForm,
		Investigating: UnderInvestigationFilterForm,
		Approving: ToQuoteFilterForm,
		Quoting: ToQuoteFilterForm,
		Closed: ClosedFilterForm,
		PreBilling: ToBillFilterForm,
		Rejected: RejectedFilterForm,
	};

	const tableComponents = {
		All: AllCaseTable,
		New: NewCaseTable,
		Investigating: UnderInvestigationTable,
		Approving: ApprovingTable,
		Quoting: ToQuoteTable,
		Closed: ClosedTable,
		PreBilling: ToBillTable,
		Rejected: RejectedTable,
	};

	let err;
	let query = queryString.parse(window.location.search);
	let data;
	let reportData;
	let caseData;
	let fetchParams;
	let hasMounted = false;

	$: activeTab = query.tab ?? "New";

	async function handleVehicleSearch(e) {
		if (e !== "") {
			try {
				if (!e || e.trim() === "") {
					err = "Please enter a vehicle no.";
					throw new Error(err);
				}
				if (e.length <= 2 || e.length >= 20) {
					err = "Vehicle no must be between 2 and 20 characters.";
					throw new Error(err);
				}
				if (!/^[a-zA-Z0-9]+$/.test(e)) {
					err = "Vehicle no must be alphanumeric.";
					throw new Error(err);
				}
				let carRes = await client(`/v1/plateNumbers/${e}?limit=100`, {
					method: "GET",
					version: "Vehicle",
				});
				let carOptions = carRes.data.map((car) => {
					return {
						value: car.vehicleId,
						label: car.plateNo,
					};
				});
				return carOptions;
			} catch (error) {
				console.log(error);
				extractErrorMessage(error);
				return [];
			}
		}
	}

	async function changeFetchParams(params) {
		if (params === "") {
			fetchParams = {
				size: $sizeSelect,
				pageNumber: 1,
			};
			initPage.set(1);
			fetchData(fetchParams);
		} else {
			let newParams = { ...fetchParams, ...params };

			if (params.sortCol || params.sortDir) {
				if (!params.page) {
					newParams.page = $initPage || 1;
				}
			}
			for (let key in newParams) {
				if (
					(Array.isArray(newParams[key]) &&
						newParams[key].length === 0) ||
					newParams[key] === ""
				) {
					delete newParams[key];
				}
			}

			fetchParams = newParams;
			fetchData(newParams);
		}
	}

	async function fetchData(params) {
		try {
			if (!params) {
				params = queryString.parse(window.location.search);
			}
			if (params.sortCol == null) {
				params.sortCol =
					activeTab === 4 ? "caseRejectedAt" : "caseCreatedAt";
				params.sortDir = "descending"; // always descending for both cases
			}

			let cleanedParams = { ...params };
			if (cleanedParams.rejectedReason === "all") {
				delete cleanedParams.rejectedReason;
			}
			loading.set(true);
			let endpoint = "";
			const { tab, ...paramsForQuery } = cleanedParams;
			let queryNav = Object.entries(paramsForQuery)
				.flatMap(([k, v]) =>
					Array.isArray(v) ? v.map((val) => [k, val]) : [[k, v]]
				)
				.map(
					([k, v]) =>
						`${encodeURIComponent(k)}=${encodeURIComponent(v)}`
				)
				.join("&");

			queryNav = `tab=${activeTab}` + (queryNav ? `&${queryNav}` : "");
			endpoint = `/v1/caseManagement/cases?`;

			if (activeTab) {
				endpoint += `status=${activeTab}`;
			}

			if (queryNav) {
				endpoint += `&${queryNav}`;
			}

			const res = await client(endpoint, {
				method: "GET",
				version: "Tasking",
			});

			navigate(`/issues/case-management?` + queryNav);

			if (res.status === 200) {
				data = res.data.data;
				fetchParams = params;
				totalRecords.set(res.data.totalCount);
				totalPages.set(res.data.totalPage);
				initPage.set(res.data.pageNumber);
			}
			loading.set(false);
		} catch (error) {
			extractErrorMessage(error);
			console.log(error);
			loading.set(false);
		}
	}

	function changeTab(tab) {
		activeTab = tab;
		navigate(`/issues/case-management?tab=${activeTab}`);
		fetchParams = { size: 20, pageNumber: 1 };
		initPage.set(1);
		fetchData(fetchParams);
	}

	function handleExportCases() {
		exportCaseByCaseNo($caseExportList);
		caseExportEnabled.set(false);
		caseExportList.set([]);
	}

	onMount(() => {
		// Fetch data when the component mounts
		fetchData();
		localStorage.removeItem("damageReviewedData");
		damageReviewedData.set([]);
	});

	const unsubscribeSizeSelect = sizeSelect.subscribe((value) => {
		if (!hasMounted) {
			hasMounted = true;
		} else {
			changeFetchParams({ size: value, page: 1, pageNumber: 1 });
		}
	});

	onDestroy(() => {
		unsubscribeSizeSelect();
	});
</script>

<Row>
	<Col xs={12}>
		<div>
			<ErrorMessageHandler message={$errorMessage} />
			<Row class="d-flex align-items-center">
				<Col>
					<PageHeader title="Case Management" />
				</Col>
				<Col class="d-flex flex-row-reverse">
					{#if $caseExportEnabled}
						<div>
							<CustomButton
								buttonText="Export"
								color="primary"
								disabled={$caseExportList.length === 0}
								on:click={() => handleExportCases()}
							/>
						</div>
						<div>
							<CustomButton
								buttonText="Cancel"
								outline={true}
								color="secondary"
								on:click={() => {
									caseExportEnabled.set(false);
									caseExportList.set([]);
								}}
							/>
						</div>
					{:else}
						<div>
							<CustomButton
								buttonText="Create new case"
								color="secondary"
								on:click={() =>
									navigate("/issues/case-management/create")}
							/>
						</div>
						{#if activeTab === "PreBilling"}
							<div>
								<CustomButton
									buttonText="Export"
									color="secondary"
									outline={true}
									on:click={() => caseExportEnabled.set(true)}
								/>
							</div>
						{/if}
					{/if}
				</Col>
			</Row>
			<Row>
				<Col>
					<Tabs {items} {activeTab} fetch={changeTab} />
				</Col>
			</Row>
			<Row>
				<svelte:component
					this={filterForms[activeTab] ?? filterForms["All"]}
					{handleVehicleSearch}
					changeSearchParams={changeFetchParams}
					params={fetchParams}
				/>
			</Row>
		</div>
	</Col>
	<Col>
		<svelte:component
			this={tableComponents[activeTab] ?? tableComponents["All"]}
			{caseData}
			{reportData}
			{data}
			{changeFetchParams}
		/>
	</Col>
</Row>
